"use client";
import { useEffect, useRef, useState, useCallback } from "react";

// Web Speech API (Chrome / Edge / Safari). Firefox doesn't support it yet.
// Defining a minimal type to avoid pulling in DOM lib types we don't need.
interface SpeechRecognitionEvent {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string; confidence: number };
  }>;
}
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

interface UseWakeWordOpts {
  // Phrases that trigger the callback. Matched case-insensitively as substrings.
  // Defaults to ["hey brain", "hey surggie", "hey surggies"].
  phrases?: string[];
  // Called when a wake phrase is detected.
  onDetect: (matchedPhrase: string) => void;
}

interface UseWakeWordReturn {
  supported: boolean;
  enabled: boolean;
  enable: () => void;
  disable: () => void;
  toggle: () => void;
  lastHeard: string | null;
  error: string | null;
}

export function useWakeWord(opts: UseWakeWordOpts): UseWakeWordReturn {
  const phrases = (opts.phrases ?? ["hey brain", "hey surggie", "hey surggies"]).map((p) =>
    p.toLowerCase()
  );
  const [enabled, setEnabled] = useState(false);
  const [lastHeard, setLastHeard] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const onDetectRef = useRef(opts.onDetect);
  const supported = !!getRecognitionCtor();

  // Keep callback ref fresh
  useEffect(() => {
    onDetectRef.current = opts.onDetect;
  });

  useEffect(() => {
    if (!enabled) {
      recRef.current?.abort();
      recRef.current = null;
      return;
    }
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setError("SpeechRecognition not supported in this browser");
      return;
    }

    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (e: SpeechRecognitionEvent) => {
      // Walk all results from this event, look for any final transcript that contains a wake phrase
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const transcript = res[0].transcript.toLowerCase().trim();
        if (transcript) setLastHeard(transcript);
        if (!res.isFinal) continue;
        const matched = phrases.find((p) => transcript.includes(p));
        if (matched) {
          // Stop the listener briefly so the push-to-talk recorder can grab the mic
          try {
            rec.stop();
          } catch { /* noop */ }
          onDetectRef.current(matched);
        }
      }
    };

    rec.onerror = (e) => {
      // "no-speech" and "aborted" are expected; ignore. Surface anything else.
      if (e.error !== "no-speech" && e.error !== "aborted") {
        setError(`SpeechRecognition error: ${e.error}`);
      }
    };

    // Auto-restart on end (browser stops the recognizer after periods of silence)
    rec.onend = () => {
      if (enabled && recRef.current === rec) {
        try {
          rec.start();
        } catch { /* already started or aborted */ }
      }
    };

    try {
      rec.start();
      recRef.current = rec;
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }

    return () => {
      rec.abort();
      if (recRef.current === rec) recRef.current = null;
    };
    // phrases is stable across renders (constructed inline) — re-running on it is fine
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const enable = useCallback(() => setEnabled(true), []);
  const disable = useCallback(() => setEnabled(false), []);
  const toggle = useCallback(() => setEnabled((v) => !v), []);

  return { supported, enabled, enable, disable, toggle, lastHeard, error };
}
